import { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { useChartTheme } from '../../../utils/chartTheme';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Filler);

/* The report's chart. The API sends the series already grouped and totalled
   (report.engine.js buildChart); this only draws it.

   One series, so slot 1 of the chart theme -- the palette's accent -- and no
   legend: the title names what is plotted. Bars by default, horizontal once
   the labels are many or long (names read sideways under a vertical axis are
   unreadable); a month grouping is a line with a 10% wash. The table under
   the chart is its twin: every value is readable there without hovering. */

const trim = (label, max) => {
  const s = String(label ?? '');
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

const ReportChart = ({ chart, formatValue }) => {
  const theme = useChartTheme();
  const isLine = chart?.kind === 'line';
  const labels = chart?.labels || [];
  const horizontal = !isLine && (labels.length > 6 || labels.some((l) => String(l).length > 14));

  const data = useMemo(() => {
    const colour = theme.series[0];
    const values = chart?.values || [];
    return {
      labels: chart?.labels || [],
      datasets: [
        isLine
          ? {
            label: chart?.valueLabel || 'Value',
            data: values,
            borderColor: colour,
            backgroundColor: theme.wash(colour, 0.1),
            borderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: colour,
            pointBorderWidth: 2,
            pointBorderColor: theme.surface,
            tension: 0.3,
            fill: true,
            spanGaps: false,
          }
          : {
            label: chart?.valueLabel || 'Value',
            data: values,
            backgroundColor: colour,
            hoverBackgroundColor: colour,
            // Rounded at the value, square at the baseline it grows from.
            borderRadius: 4,
            borderSkipped: 'start',
            maxBarThickness: 24,
          },
      ],
    };
  }, [chart, isLine, theme]);

  const options = useMemo(() => {
    const tooltip = {
      ...theme.tooltip,
      callbacks: {
        label: (ctx) => {
          const raw = horizontal ? ctx.parsed.x : ctx.parsed.y;
          return ` ${formatValue(raw)}`;
        },
      },
    };
    const valueAxis = theme.valueAxis({
      beginAtZero: true,
      ticks: { callback: (v) => formatValue(v) },
    });
    const categoryAxis = theme.categoryAxis({
      ticks: {
        callback(value) {
          return trim(this.getLabelForValue(value), horizontal ? 28 : 14);
        },
        maxRotation: 0,
        autoSkip: true,
      },
    });
    return {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: horizontal ? 'y' : 'x',
      interaction: { mode: 'index', intersect: false },
      animation: { duration: 300 },
      plugins: { legend: { display: false }, tooltip },
      scales: horizontal ? { x: valueAxis, y: categoryAxis } : { x: categoryAxis, y: valueAxis },
    };
  }, [horizontal, formatValue, theme]);

  if (!chart || !labels.length) {
    return <p className="rg-chart-empty">Nothing to chart: no rows match these conditions.</p>;
  }

  const height = horizontal ? Math.max(220, labels.length * 30 + 56) : 280;
  const described = labels
    .map((label, i) => `${label} ${formatValue(chart.values[i])}`)
    .join(', ');

  return (
    <figure className="rg-chart">
      <figcaption className="rg-chart-head">
        <span className="rg-chart-title">{chart.title}</span>
        {chart.note && <span className="rg-chart-note">{chart.note}</span>}
      </figcaption>
      <div
        className="rg-chart-plot"
        role="img"
        aria-label={`${chart.title}: ${described}`}
        style={{ height: `${height}px` }}
      >
        {isLine ? <Line data={data} options={options} /> : <Bar data={data} options={options} />}
      </div>
    </figure>
  );
};

export default ReportChart;
